<template>
  <transition-group name="Transition__opacity-fade">
    <div
      v-for="(alert, index) in dataAlerts"
      :key="`alert-${index}`"
      :class="['BaseAlerts row row--width-auto row--align-center row--justify-center', {
        'BaseAlerts--fixed': fixed,
      }]"
      :ref="`BaseAlerts`"
    >
      <div
        v-if="!hideIcon"
        class="BaseAlerts__icon-container mr-10"
      >
        <i class="fa fa-info fa--none danger" />
      </div>

      <div class="column mr-10">
        <div class="row">
          <label :class="[
            'BaseAlerts__message fc-white fs-14 fw-med',
            {
              'BaseAlerts__message--centered': centerMessage
            }
          ]">
            {{ alert.message }}
          </label>
        </div>
        <div
          v-if="alert.submessage"
          class="row"
        >
          <label class="fc-white fs-14">{{ alert.submessage }}</label>
        </div>
      </div>

      <div class="row row--width-auto">
        <base-button
          v-if="alert.route"
          @click.capture="closeAlert(index)"
          class="bg-white fc-red"
        >
          {{ alert.action }}
        </base-button>

        <button
          v-if="closeable"
          @click="removeAlert(index)"
          class="BaseAlerts__close-icon-btn ml-10"
        >
          <i class="fa fa-add fa-10 fa-rotate-45 fc-white fa--none" />
        </button>
      </div>
    </div>
  </transition-group>
</template>

<script>
// Helpers
import {
  calculateLeftPosition,
  calculateTopPosition,
} from '../utils/helpers'
// Components
import BaseButton from './base-button.vue'

export default {
  name: 'BaseAlerts',

  components: {
    BaseButton,
  },

  props: {
    // Alerts should contain:
    // action: call to action button text
    // closeable: whether a user can click "X" to hide the notification
    // message: main message to display to the user in heavy weight
    // route: where to send the user to resolve the notification, if applicable
    // submessage: an optional message to display under message
    // type: what kind of announcement is it (used to hide banking alerts)
    alerts: {
      type: Array,
      required: true,
    },
    // Mostly to be used when fixed is true
    centerMessage: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Pass true if you want to use label as just a text-based alert
    // prevents user from closing/hiding alert
    closeable: {
      type: Boolean,
      required: false,
      default: false,
    },
    fixed: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  // Easier to access <body> via a bit of JS than making some confusing CSS hack
  mounted () {
    if (this.fixed) {
      return
    }

    // Hide banks if route is the banking dashboard
    this.hideBankingAlerts(this.$route)

    // Set the left, top, and width inline styles of the alerts
    this.setPositions()

    // Alerts should be responsive
    window.addEventListener('resize', this.setPositions)
  },

  beforeDestroy () {
    if (this.fixed) {
      return
    }

    window.removeEventListener('resize', this.setPositions)
  },

  data () {
    return {
      dataAlerts: this.alerts,
    }
  },

  computed: {
    mobile () {
      return this.$store.getters.mobile
    },
  },

  watch: {
    $route (newRoute) {
      // Hide banks if route is the banking dashboard
      this.hideBankingAlerts(newRoute)
    },
  },

  methods: {
    closeAlert (index) {
      if (this.dataAlerts[index].route) {
        this.$router.push(this.dataAlerts[index].route)
      }

      this.removeAlert(index)
    },

    hideBankingAlerts (route) {
      if (route.name === 'dashboard-client-banking') {

        this.dataAlerts = this.dataAlerts.filter((alert) => {
          return alert.type !== 'banking'
        })

        // Without nextTick, spacing will be off when positions are recalculated since the UI hasn't updated
        this.$nextTick(() => {
          this.setPositions()
        })
      }
    },

    removeAlert (index) {
      this.dataAlerts = [ ...this.dataAlerts.slice(0, index), ...this.dataAlerts.slice(index + 1) ]

      // Without nextTick, spacing will be off when positions are recalculated since the UI hasn't updated
      this.$nextTick(() => {
        this.setPositions()
      })
    },

    setLeft () {
      this.$refs.BaseAlerts.forEach((el) => {
        el.style.left = calculateLeftPosition(el)
      })
    },

    setPositions () {
      // Don't overwrite the CSS of fixed alerts (this is required because watching the route)
      if (this.fixed) {
        return
      }

      if (!this.$refs.BaseAlerts.length) {
        return
      }

      this.setWidth()
      this.setLeft()
      this.setTop()
    },

    setTop () {
      // Since multiple alerts could be displayed at a time, we want to stack them appropriately
      // IF index === 0, top = 50
      // IF index !== 0, top = (50 + ref.offsetHeight) * this.index
      // If mobile, we want to give it a little more room for dynamic content in the modal, so less top
      // https://bobtail.atlassian.net/browse/SM-266

      let currentDistanceFromTop = 0 // Measurement of the bottom of the last displayed alert to the top

      this.$refs.BaseAlerts.forEach((el, index) => {
        const alertHeight = this.$refs.BaseAlerts[index].offsetHeight
        const spacing = 10

        el.style.top = calculateTopPosition(index, spacing, currentDistanceFromTop)

        currentDistanceFromTop += spacing + alertHeight
      })
    },

    setWidth () {
      const maxWidth = 1440 * 0.85
      const windowWidth = window.innerWidth

      this.$refs.BaseAlerts.forEach((el) => {
        el.style.width = `${Math.min(windowWidth * 0.85, maxWidth)}px`
      })
    },
  },
}
</script>

<style lang="sass" scoped>
.BaseAlerts
  background-color: $danger
  border-radius: $border-radius
  // left: 0
  // max-width: rem($xlarge)
  padding: rem(10px)
  position: fixed // Default
  top: 0 // Default
  // width: 85% // BaseAlerts should be a row, but we want to overwrite the default width
  z-index: 5 // Highest z-index app-wide

  &--fixed
    max-width: unset
    position: static // Allow position being set in another component
    top: unset // Allow top being set in another component
    width: 100% // Allow top being set in another component

  &__close-icon-btn
    height: rem(38px)
    width: rem(38px)

  &__icon-container
    background-color: $white
    border-radius: $border-radius
    padding: rem(9px)

  &__message
    &--centered
      margin: 0 auto
</style>
